/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { AxiosResponse, RawAxiosRequestHeaders } from 'axios';
import { CoreNetworking, type ApiClient } from '@airasia-rn/ui/core-networking';
import { Environment, FeaturedDestination, LogErrorFn } from '../models';
import type {
  BannerApiResponse,
  SpecialReqApiResponse,
  StaticApiServiceConfig,
  PopularSearchesResponse,
} from '../types';

export class StaticApiService {
  private readonly baseClient: ApiClient;
  private readonly logError?: LogErrorFn;
  private readonly environment: Environment;

  constructor(config: StaticApiServiceConfig = {}) {
    const { environment = 'stg', logError } = config;
    this.environment = environment;
    this.logError = logError;
    this.baseClient = CoreNetworking({
      baseUrl: this.getHost(),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/plain, */*',
      },
    });
  }

  private getHost(): string {
    return this.environment === 'stg'
      ? 'https://static.stgairasia.com'
      : 'https://static.airasia.com';
  }

  protected async get<T>(
    url: string,
    params?: unknown,
    headers?: RawAxiosRequestHeaders,
  ): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.baseClient.get(url, {
        params,
        headers,
      });
      return response.data;
    } catch (error) {
      this.handleError(error, { url, method: 'GET' });
      throw error;
    }
  }

  private handleError(error: unknown, context?: Record<string, any>): void {
    if (this.logError) {
      this.logError(error, context);
    } else {
      console.error('StaticBucketApiService Error:', error, context);
    }
  }

  /**
   * Fetch featured destinations for hotels
   */
  public async getFeaturedDestinations(): Promise<FeaturedDestination[]> {
    try {
      return await this.get<FeaturedDestination[]>(
        '/hotels/featured-destinations/Move/hotels_featured-destinations_file.json',
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetch hotel banners from static bucket
   */
  public async fetchBanners(): Promise<BannerApiResponse> {
    return this.get<BannerApiResponse>(
      `/hotels/country-banner/searchwidget/${this.environment}/file.json`,
    );
  }

  /**
   * Fetch Special request from user for room using static bucket
   */
  public async fetchSpecialRequest(): Promise<SpecialReqApiResponse> {
    return this.get<SpecialReqApiResponse>(
      `hotels/movepages/specialrequests/file.json`,
    );
  }

  /**
   * Fetch Popular searches for hotels
   */
  public async fetchPopularSearches(): Promise<PopularSearchesResponse> {
    return this.get<PopularSearchesResponse>(
      `/hotels/movepages/popularsearches/file.json`,
    );
  }
}
